import { useEffect, useMemo, useState } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Copy, Mail } from 'lucide-react';

export default function EmailsPage() {
  const [emails, setEmails] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const selected = emails.find(e => e.id === selectedId) || null;
  const [values, setValues] = useState({});
  const [rendered, setRendered] = useState(null);

  useEffect(() => { api.get('/templates/emails').then(r => { setEmails(r.data || []); if (r.data?.[0]) setSelectedId(r.data[0].id); }).catch(e => toast.error(parseErr(e))); }, []);

  const placeholders = useMemo(() => {
    if (!selected) return [];
    const text = (selected.metadata?.subject || '') + ' ' + (selected.blocks || []).map(b => b?.text || '').join(' ');
    const s = new Set(); const re = /\{([a-zA-Z0-9_]+)\}/g; let m;
    while ((m = re.exec(text))) s.add(m[1]);
    return Array.from(s);
  }, [selected]);

  useEffect(() => { setValues({}); setRendered(null); }, [selectedId]);

  const fill = async () => {
    if (!selected) return;
    try {
      const r = await api.post(`/templates/emails/${selected.id}/fill`, { values });
      setRendered(r.data);
    } catch (e) { toast.error(parseErr(e)); }
  };
  const copyRendered = () => {
    if (!rendered) return;
    const txt = `Subject: ${rendered.subject}\n\n${rendered.body}`;
    navigator.clipboard.writeText(txt);
    toast.success('Copied to clipboard');
  };

  return (
    <AppShell breadcrumbs={[{ label: 'Email Templates' }]}>
      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr]" data-testid="emails-page">
        <aside className="border-r bg-[hsl(var(--surface-1))] p-3 max-h-[calc(100vh-56px)] overflow-y-auto" style={{ borderColor: 'hsl(var(--border))' }}>
          <div className="text-[11px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))] px-1 py-1 font-semibold">Templates</div>
          {emails.map(e => (
            <button key={e.id} onClick={() => setSelectedId(e.id)} className={`side-nav-item w-full ${selectedId === e.id ? 'active' : ''}`} data-testid={`email-select-${e.id}`}>
              <Mail size={13} /><span className="truncate">{e.title}</span>
            </button>
          ))}
        </aside>
        <div className="p-6 max-w-[900px]">
          {!selected ? <div className="text-[hsl(var(--foreground-muted))]">Choose a template.</div> : (
            <>
              <h1 className="font-heading text-2xl font-semibold">{selected.title}</h1>
              <div className="text-[12px] text-[hsl(var(--foreground-muted))] font-mono mt-1">Subject: {selected.metadata?.subject}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                {placeholders.map(k => (
                  <label key={k} className="block">
                    <span className="text-[12px] text-[hsl(var(--foreground-muted))] font-mono">{`{${k}}`}</span>
                    <Input value={values[k] || ''} onChange={ev => setValues(v => ({ ...v, [k]: ev.target.value }))} data-testid={`email-placeholder-${k}`} />
                  </label>
                ))}
              </div>
              <div className="mt-3 flex gap-2">
                <Button onClick={fill} data-testid="email-fill-button">Preview filled email</Button>
                {rendered && <Button variant="outline" onClick={copyRendered} data-testid="email-copy"><Copy size={13} className="mr-1" />Copy</Button>}
              </div>
              {rendered && (
                <div className="mt-4 border rounded-xl bg-[hsl(var(--card))] p-4" style={{ borderColor: 'hsl(var(--border))' }} data-testid="email-rendered">
                  <div className="font-heading font-semibold">{rendered.subject}</div>
                  {rendered.placeholders_masked && (
                    <div className="mt-1 text-[12px] chip chip-warning inline-flex">Amount fields masked for your role</div>
                  )}
                  <div className="mt-3 whitespace-pre-wrap text-[14px] leading-relaxed">{rendered.body}</div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </AppShell>
  );
}
